"use client";

import * as React from "react";
import type { Row, Table } from "@tanstack/react-table";
import { Checkbox, cn } from "@aio/platform-ui";

/**
 * Shared anchor state for shift-click range selection.
 *
 * The "last clicked rowId" must be visible to every {@link SelectionCell}
 * rendered in the same table body, otherwise each cell would only see its
 * own click history. The provider below holds a single mutable ref that all
 * cells read/write. We use a ref (not state) because anchor changes do NOT
 * need to trigger a re-render — only the next click consumes the anchor and
 * dispatches a selection update.
 */
interface SelectionAnchorContextValue {
  /** Get the currently-stored anchor rowId, or null if no row has been clicked yet. */
  getAnchorId: () => string | null;
  /** Replace the anchor with the given rowId. */
  setAnchorId: (id: string | null) => void;
}

const SelectionAnchorContext =
  React.createContext<SelectionAnchorContextValue | null>(null);

/**
 * Wraps the table body so every {@link SelectionCell} inside shares one
 * anchor for shift-click range selection.
 *
 * The anchor is the rowId of the last *plain* checkbox click. A subsequent
 * shift-click on another row toggles every row from the anchor to the
 * shift-clicked row, inclusive, in display order.
 */
export function SelectionAnchorProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const anchorRef = React.useRef<string | null>(null);
  const value = React.useMemo<SelectionAnchorContextValue>(
    () => ({
      getAnchorId: () => anchorRef.current,
      setAnchorId: (id) => {
        anchorRef.current = id;
      },
    }),
    [],
  );
  return (
    <SelectionAnchorContext.Provider value={value}>
      {children}
    </SelectionAnchorContext.Provider>
  );
}

/**
 * Internal helper: read the anchor context, returning a no-op stub when the
 * cell renders outside a {@link SelectionAnchorProvider}. The cell still
 * works without the provider — shift-click simply degrades to a single-row
 * toggle, which matches the "first ever click" behaviour anyway.
 */
function useSelectionAnchor(): SelectionAnchorContextValue {
  const ctx = React.useContext(SelectionAnchorContext);
  if (ctx) return ctx;
  // Fallback: anchor never persists. Shift-click degrades gracefully to a
  // single-row toggle. This is intentional, not a silent failure — see the
  // ProviderProvider above for the recommended placement.
  return {
    getAnchorId: () => null,
    setAnchorId: () => {},
  };
}

/**
 * Compute the inclusive range of row ids between two anchors using the
 * table's current display order. Returns the ids in display order so the
 * caller can apply them deterministically.
 *
 * If either anchor is missing from the current row set (e.g. it was filtered
 * out since the last click), we return only the target id — selecting a
 * range that crosses a filtered-out anchor is an unsafe assumption.
 */
function rangeBetween<TData>(
  table: Table<TData>,
  anchorId: string,
  targetId: string,
): string[] {
  const ordered = table.getRowModel().rows;
  const anchorIdx = ordered.findIndex((r) => r.id === anchorId);
  const targetIdx = ordered.findIndex((r) => r.id === targetId);
  if (anchorIdx === -1 || targetIdx === -1) return [targetId];
  const [lo, hi] =
    anchorIdx <= targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
  return ordered.slice(lo, hi + 1).map((r) => r.id);
}

/**
 * Props for {@link SelectionCell}.
 */
export interface SelectionCellProps<TData> {
  /** The TanStack row this cell belongs to. */
  row: Row<TData>;
  /** The owning table — needed for shift-click range selection. */
  table: Table<TData>;
  /** Extra classes for the wrapping `<td>` element. */
  className?: string;
  /**
   * Override for the checkbox's accessible label. Defaults to
   * "Select row" / "Deselect row" depending on current state.
   */
  ariaLabel?: string;
}

/**
 * One `<td>` containing a checkbox for selecting/deselecting a single row.
 *
 * Behaviour:
 * - Plain click toggles the row and updates the shared anchor.
 * - Shift+click toggles every row between the anchor and this row,
 *   inclusive, in display order. The new state for the range is "selected"
 *   when the clicked row is becoming selected, and "unselected" otherwise —
 *   matching the visual expectation of "make the range match the row I just
 *   clicked".
 * - Click events on the cell `<td>` itself never bubble — the row's own
 *   click handler (DataRow) must not interpret a checkbox click as an
 *   "activate row" gesture.
 *
 * Must be rendered inside a {@link SelectionAnchorProvider} for shift-click
 * to work across cells; without one, shift-click degrades to a single-row
 * toggle.
 */
export function SelectionCell<TData>({
  row,
  table,
  className,
  ariaLabel,
}: SelectionCellProps<TData>): React.JSX.Element {
  const anchor = useSelectionAnchor();

  const isSelected = row.getIsSelected();
  const canSelect = row.getCanSelect();

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Stop propagation so DataRow's click handler doesn't also fire.
      event.stopPropagation();

      const anchorId = anchor.getAnchorId();
      if (event.shiftKey && anchorId && anchorId !== row.id) {
        // Range select: toggle every row from anchor to current to the
        // OPPOSITE of the current row's state. The user clicked a row that
        // was unselected -> they want to select the range; clicked one that
        // was selected -> they want to deselect the range.
        const ids = rangeBetween(table, anchorId, row.id);
        const desired = !isSelected;
        const next = { ...table.getState().rowSelection };
        for (const id of ids) {
          if (desired) {
            next[id] = true;
          } else {
            delete next[id];
          }
        }
        table.setRowSelection(next);
        // Update anchor to the row the user just shift-clicked so a follow-on
        // shift-click extends from the most recent endpoint.
        anchor.setAnchorId(row.id);
        return;
      }

      // Plain click: toggle this row only and become the new anchor.
      row.toggleSelected();
      anchor.setAnchorId(row.id);
    },
    [anchor, isSelected, row, table],
  );

  return (
    <td
      className={cn("w-10 px-3 py-2 align-middle", className)}
      // Don't let a stray click on padding around the checkbox bubble up to
      // the row activate handler. The checkbox itself stops propagation in
      // its onClick — this guards the surrounding cell area.
      onClick={(e) => e.stopPropagation()}
      data-testid={`selection-cell-${row.id}`}
    >
      <Checkbox
        checked={isSelected}
        disabled={!canSelect}
        onClick={handleClick}
        aria-label={ariaLabel ?? (isSelected ? "Deselect row" : "Select row")}
        data-testid={`selection-cell-checkbox-${row.id}`}
      />
    </td>
  );
}
